import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  Stack,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Iconify from "../components/iconify";
import { get, del, post } from "src/utils/networkUtils";
import { validateJoinCode } from "src/utils/joinCodeValidation";

const LEAGUE_TYPES = [
  { value: "mini_league", label: "Mini League" },
  { value: "club", label: "Club" },
  { value: "national", label: "National" },
];

const EMPTY_FORM = {
  name: "",
  description: "",
  type: "mini_league",
  country_code: "",
  club_id: "",
  tournament_calendar_id: "",
  start_gameweek: 1,
  is_public: true,
  is_celebrity: false,
  commissioner_user_id: "",
};

const TYPE_COLORS = {
  mini_league: "info",
  club: "warning",
  national: "success",
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function EcffLeaguesPage() {
  const [leagues, setLeagues] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  // row actions popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeLeague, setActiveLeague] = useState(null);

  // detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinCodeTouched, setJoinCodeTouched] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [calendarsLoading, setCalendarsLoading] = useState(false);

  const LIMIT = 20;

  const load = useCallback((p, q) => {
    setLeagues(null);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (q) params.set("search", q);
    get(`/admin/leagues/?${params}`)
      .then((r) => {
        const items = r?.data?.items ?? r?.items ?? [];
        const t =
          r?.data?.pagination?.total ?? r?.pagination?.total ?? items.length;
        setLeagues(Array.isArray(items) ? items : []);
        setTotal(t);
      })
      .catch(() => setLeagues([]));
  }, []);

  useEffect(() => {
    setPage(1);
    load(1, debouncedSearch);
  }, [debouncedSearch, load]);

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_FORM);
    setCreateError("");
    setJoinCode("");
    setJoinCodeTouched(false);
    setCreateOpen(true);
    setCalendarsLoading(true);
    get("/admin/leagues/tournament-calendars")
      .then((r) => setCalendars(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setCalendars([]))
      .finally(() => setCalendarsLoading(false));
  };

  const handleCreateField = (field) => (e) =>
    setCreateForm((f) => ({ ...f, [field]: e.target.value }));

  const handleCreateToggle = (field) => (e) =>
    setCreateForm((f) => ({ ...f, [field]: e.target.checked }));

  const handleCreateSubmit = async () => {
    setCreateError("");
    if (!createForm.name.trim()) {
      setCreateError("League name is required.");
      return;
    }
    if (createForm.is_celebrity) {
      const joinCodeValidationError = validateJoinCode(joinCode);
      setJoinCodeTouched(true);
      if (joinCodeValidationError) {
        return;
      }
    }
    const countryCode = createForm.country_code.trim().toUpperCase();
    const clubId = createForm.club_id.trim();
    // National/club leagues auto-enrol players by matching these keys against their
    // profile, and only work inside a tournament calendar — validate before sending.
    if (createForm.type === "national" || createForm.type === "club") {
      if (!createForm.tournament_calendar_id) {
        setCreateError(
          "National and club leagues require a tournament calendar."
        );
        return;
      }
      if (
        createForm.type === "national" &&
        !/^[A-Za-z]{3}$/.test(countryCode)
      ) {
        setCreateError("A 3-letter country code is required (e.g. ENG).");
        return;
      }
      if (createForm.type === "club" && !clubId) {
        setCreateError("A club id is required for a club league.");
        return;
      }
    }
    setCreating(true);
    try {
      const body = {
        name: createForm.name.trim(),
        type: createForm.type,
        start_gameweek: Number(createForm.start_gameweek),
        is_public: createForm.is_public,
        is_celebrity: createForm.is_celebrity,
      };
      if (createForm.type === "national") body.country_code = countryCode;
      if (createForm.type === "club") body.club_id = clubId;
      if (createForm.tournament_calendar_id)
        body.tournament_calendar_id = createForm.tournament_calendar_id;
      if (createForm.description.trim())
        body.description = createForm.description.trim();
      if (createForm.commissioner_user_id.trim())
        body.commissioner_user_id = createForm.commissioner_user_id.trim();
      if (createForm.is_celebrity && joinCode) body.join_code = joinCode;
      await post("/admin/leagues/", body);
      setCreateOpen(false);
      load(1, debouncedSearch);
      setPage(1);
    } catch (err) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to create league.";
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenMenu = (e, league) => {
    setAnchorEl(e.currentTarget);
    setActiveLeague(league);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleViewDetail = () => {
    handleCloseMenu();
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    get(`/admin/leagues/${activeLeague.id}`)
      .then((r) => setDetail(r?.data ?? null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  const handleDeleteClick = () => {
    setDeleteTarget(activeLeague);
    handleCloseMenu();
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await del(`/admin/leagues/${deleteTarget.id}`);
      setLeagues((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setTotal((t) => t - 1);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const joinCodeError = createForm.is_celebrity
    ? validateJoinCode(joinCode)
    : "";

  return (
    <>
      <Helmet>
        <title>Leagues | EngageCraft Fantasy Football Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4">
            Leagues{total > 0 && ` (${total})`}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleOpenCreate}
          >
            Create League
          </Button>
        </Stack>

        <Card>
          <Box sx={{ p: 2 }}>
            <TextField
              size="small"
              placeholder="Search by league name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:search-fill"
                      sx={{ color: "text.disabled" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <Iconify icon="eva:close-fill" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: 280 }}
            />
          </Box>

          {leagues === null ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Join Code</TableCell>
                    <TableCell>Commissioner</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Start GW</TableCell>
                    <TableCell>Public</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leagues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        {search
                          ? `No leagues matching "${search}".`
                          : "No leagues yet."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    leagues.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {l.name ?? "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {String(l.id ?? "").slice(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={l.type ?? "-"}
                            size="small"
                            color={TYPE_COLORS[l.type] ?? "default"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {l.join_code ?? "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>{l.commissioner_username ?? "-"}</TableCell>
                        <TableCell>{l.member_count ?? "-"}</TableCell>
                        <TableCell>{l.start_gameweek ?? "-"}</TableCell>
                        <TableCell>{l.is_public ? "Yes" : "No"}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleOpenMenu(e, l)}>
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                p: 2,
              }}
            >
              <Button
                size="small"
                disabled={page === 1}
                onClick={() => {
                  const p = page - 1;
                  setPage(p);
                  load(p, debouncedSearch);
                }}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page >= totalPages}
                onClick={() => {
                  const p = page + 1;
                  setPage(p);
                  load(p, debouncedSearch);
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </Card>
      </Container>

      {/* Row actions menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleViewDetail}>
          <Iconify icon="eva:eye-fill" sx={{ mr: 1 }} />
          View details
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          <Iconify icon="eva:trash-2-outline" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Popover>

      {/* Detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {detailLoading || !detail ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            {detailLoading ? (
              <CircularProgress />
            ) : (
              <Typography>Failed to load league.</Typography>
            )}
          </Box>
        ) : (
          <>
            <DialogTitle>
              {detail.name}
              <Chip
                label={detail.type}
                size="small"
                color={TYPE_COLORS[detail.type] ?? "default"}
                sx={{ ml: 1.5, verticalAlign: "middle" }}
              />
            </DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={4} mb={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Join Code
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  >
                    {detail.join_code}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Commissioner
                  </Typography>
                  <Typography variant="body2">
                    {detail.commissioner_username ?? "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Start Gameweek
                  </Typography>
                  <Typography variant="body2">
                    {detail.start_gameweek}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Members
                  </Typography>
                  <Typography variant="body2">{detail.member_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Public
                  </Typography>
                  <Typography variant="body2">
                    {detail.is_public ? "Yes" : "No"}
                  </Typography>
                </Box>
              </Stack>

              {detail.description && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {detail.description}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" mb={1}>
                Members
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Rank</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.members ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No members yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (detail.members ?? []).map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.username ?? "-"}</TableCell>
                        <TableCell>{m.team_name ?? "-"}</TableCell>
                        <TableCell>{m.rank ?? "-"}</TableCell>
                        <TableCell>{m.points ?? "-"}</TableCell>
                        <TableCell>
                          {m.joined_at
                            ? new Date(m.joined_at).toLocaleDateString()
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
              <Button
                color="error"
                onClick={() => {
                  setDeleteTarget(detail);
                  setDetailOpen(false);
                  setDeleteOpen(true);
                }}
              >
                Delete league
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
      >
        <DialogTitle>Delete league?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.name}</strong> and all its entries will be
            permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create League dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create League</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <TextField
              label="League Name"
              required
              value={createForm.name}
              onChange={handleCreateField("name")}
              inputProps={{ maxLength: 30 }}
              size="small"
              fullWidth
            />

            <TextField
              label="Description"
              value={createForm.description}
              onChange={handleCreateField("description")}
              inputProps={{ maxLength: 100 }}
              size="small"
              fullWidth
              multiline
              rows={2}
            />

            <FormControl size="small" fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={createForm.type}
                label="Type"
                onChange={handleCreateField("type")}
              >
                {LEAGUE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {createForm.type === "national" && (
              <TextField
                label="Country Code"
                required
                value={createForm.country_code}
                onChange={handleCreateField("country_code")}
                inputProps={{
                  maxLength: 3,
                  style: { textTransform: "uppercase" },
                }}
                size="small"
                fullWidth
                placeholder="3-letter code, e.g. ENG"
                helperText="Auto-enrols players whose profile country matches this code."
              />
            )}

            {createForm.type === "club" && (
              <TextField
                label="Club"
                required
                value={createForm.club_id}
                onChange={handleCreateField("club_id")}
                inputProps={{ maxLength: 255 }}
                size="small"
                fullWidth
                placeholder="Club name, exactly as on player profiles"
                helperText="Auto-enrols players whose favourite club matches this value."
              />
            )}

            {(() => {
              const calendarRequired =
                createForm.type === "national" || createForm.type === "club";
              const calendarLabel = calendarRequired
                ? "Tournament Calendar"
                : "Tournament Calendar (optional)";
              return (
                <FormControl size="small" fullWidth required={calendarRequired}>
                  <InputLabel>{calendarLabel}</InputLabel>
                  <Select
                    value={createForm.tournament_calendar_id}
                    label={calendarLabel}
                    onChange={handleCreateField("tournament_calendar_id")}
                    disabled={calendarsLoading}
                  >
                    {!calendarRequired && (
                      <MenuItem value="">None — global league</MenuItem>
                    )}
                    {calendarsLoading ? (
                      <MenuItem disabled>Loading…</MenuItem>
                    ) : (
                      calendars.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {[c.competition_name, c.season_label]
                            .filter(Boolean)
                            .join(" — ") || c.id}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              );
            })()}

            <TextField
              label="Start Gameweek"
              required
              type="number"
              value={createForm.start_gameweek}
              onChange={handleCreateField("start_gameweek")}
              inputProps={{ min: 1 }}
              size="small"
              fullWidth
            />

            <TextField
              label="Commissioner User ID (optional)"
              value={createForm.commissioner_user_id}
              onChange={handleCreateField("commissioner_user_id")}
              size="small"
              fullWidth
              placeholder="UUID — leave blank for admin-owned league"
            />

            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.is_public}
                    onChange={handleCreateToggle("is_public")}
                  />
                }
                label="Public"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.is_celebrity}
                    onChange={handleCreateToggle("is_celebrity")}
                  />
                }
                label="Celebrity"
              />
            </Stack>

            {createForm.is_celebrity && (
              <TextField
                label="Join Code"
                value={joinCode}
                onChange={(event) =>
                  setJoinCode(event.target.value.toUpperCase())
                }
                onBlur={() => setJoinCodeTouched(true)}
                error={joinCodeTouched && Boolean(joinCodeError)}
                helperText={
                  joinCodeTouched && joinCodeError
                    ? joinCodeError
                    : "Optional. Use 6 to 8 alphabets, without spaces or inappropriate language."
                }
                inputProps={{
                  minLength: 6,
                  maxLength: 8,
                  pattern: "[A-Za-z]{6,8}",
                  autoCapitalize: "characters",
                  spellCheck: false,
                }}
                size="small"
                fullWidth
              />
            )}

            {createError && (
              <Typography variant="body2" color="error">
                {createError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={creating}
          >
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
